import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessagesSquare, ChevronDown } from "lucide-react";
import { getPlaceFaq } from "@/lib/api";
import { T } from "@/context/LanguageContext";

export function PlaceFAQ({ geoId, placeName }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    setItems([]); setOpen(null);
    getPlaceFaq(geoId).then((d) => { if (alive) setItems(d.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, [geoId]);

  if (!items.length) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  return (
    <section className="mx-auto max-w-[1200px] px-4 pb-16" id="tab-faq" data-testid="place-faq">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="mb-6 flex items-center gap-3 border-b border-border pb-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-white">
          <MessagesSquare className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-3xl font-black text-navy"><T>People Also Ask</T></h2>
          <p className="text-xs text-muted-foreground"><T>Real questions visitors asked about</T> {placeName}</p>
        </div>
      </div>
      <div className="space-y-2.5">
        {items.map((it, i) => {
          const isOpen = open === it.id;
          return (
            <div key={it.id} className="overflow-hidden rounded-xl border border-border bg-white" data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(isOpen ? null : it.id)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-parchment/50"
                data-testid={`faq-question-${i}`}
                aria-expanded={isOpen}
              >
                <span className="font-display text-base font-bold text-navy">{it.q}</span>
                <ChevronDown className={`h-5 w-5 shrink-0 text-oldglory-red transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <p className="border-t border-border px-5 py-4 text-[15px] leading-relaxed text-ink/85" data-testid={`faq-answer-${i}`}>
                      <T>{it.a}</T>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground">
        <T>Compiled and refined by USAPulse AI from visitor questions. Verify important details with official sources.</T>
      </p>
    </section>
  );
}
